import { useMemo } from 'react';
import { Archive, CheckCircle2, Database, Download, FileSearch, FileSpreadsheet, LockKeyhole, RefreshCw, RotateCcw, ShieldCheck, TriangleAlert } from 'lucide-react';
import { penetrationByGeography, terminalRows } from '../lib/analytics';
import { exportDetails, exportFullPackage, exportPenetration, exportTerminals } from '../lib/exporter';
import { formatCompactCurrency, formatDate, formatDateTime, formatNumber } from '../lib/format';
import type { DashboardDataset, SalesRecord, TargetConfig } from '../types';

interface DataViewProps {
  dataset: DashboardDataset;
  records: SalesRecord[];
  targets: TargetConfig;
  dateLabel: string;
  onUpload: () => void;
  onRestoreDemo: () => void;
}

export default function DataView({ dataset, records, targets, dateLabel, onUpload, onRestoreDemo }: DataViewProps) {
  const terminals = useMemo(() => terminalRows(records, targets.targetProduct), [records, targets.targetProduct]);
  const penetration = useMemo(() => penetrationByGeography(records, targets.targetProduct), [records, targets.targetProduct]);
  const returns = records.filter((record) => record.quantity < 0 || record.movementType.includes('退')).length;
  const duplicateCount = records.length - new Set(records.map((record) => record.salesId)).size;
  const missingCore = records.filter((record) => !record.product || !record.saleDate || !record.customerName).length;
  const netSales = records.reduce((sum, record) => sum + record.internalAmount, 0);
  const qualityScore = Math.max(0, 100 - duplicateCount * 2 - missingCore * 5 - dataset.meta.warnings.length * 3);

  return (
    <div className="view-stack">
      <section className="data-hero">
        <div className="data-file-icon"><FileSpreadsheet size={28} /></div>
        <div className="data-file-copy">
          <div className="data-file-title"><h2>{dataset.meta.fileName}</h2><span className={`source-pill ${dataset.meta.demo ? 'demo' : 'live'}`}>{dataset.meta.demo ? '演示数据' : '已上传数据'}</span></div>
          <p>{dataset.meta.sheetName} · 表头第 {dataset.meta.headerRow} 行 · {formatDate(dataset.meta.dateMin)}—{formatDate(dataset.meta.dateMax)}</p>
          <span>导入于 {formatDateTime(dataset.meta.importedAt)}</span>
        </div>
        <button className="primary-button" type="button" onClick={onUpload}><RefreshCw size={17} /> 上传新 Excel</button>
      </section>

      <section className="data-stat-grid">
        <article><span><Database size={17} /> 有效记录</span><strong>{formatNumber(dataset.meta.rowCount)}</strong><small>当前源数据</small></article>
        <article><span><ShieldCheck size={17} /> 数据质量</span><strong>{qualityScore} 分</strong><small>{qualityScore >= 95 ? '字段完整，可直接分析' : '建议查看提醒'}</small></article>
        <article><span><TriangleAlert size={17} /> 退货记录</span><strong>{formatNumber(returns)}</strong><small>已按净额计算</small></article>
        <article><span><Archive size={17} /> 当前净销售额</span><strong>{formatCompactCurrency(netSales)}</strong><small>随全局筛选变化</small></article>
      </section>

      <section className="download-grid">
        <article className="download-card featured">
          <span className="download-icon"><Archive size={21} /></span>
          <div><span className="eyebrow">推荐</span><h3>完整分析包</h3><p>销售明细、终端分析、单品渗透和数据说明，一次下载。</p></div>
          <button className="primary-button" type="button" onClick={() => void exportFullPackage(dataset, records, terminals, penetration, targets)}><Download size={16} /> 下载</button>
        </article>
        <article className="download-card">
          <span className="download-icon blue"><FileSearch size={21} /></span><div><h3>筛选后明细</h3><p>{formatNumber(records.length)} 条记录</p></div>
          <button className="icon-button" type="button" onClick={() => void exportDetails(records, dateLabel)} aria-label="下载筛选后明细"><Download size={17} /></button>
        </article>
        <article className="download-card">
          <span className="download-icon teal"><Database size={21} /></span><div><h3>终端维度数据</h3><p>{formatNumber(terminals.length)} 家终端</p></div>
          <button className="icon-button" type="button" onClick={() => void exportTerminals(terminals, dateLabel)} aria-label="下载终端数据"><Download size={17} /></button>
        </article>
        <article className="download-card">
          <span className="download-icon violet"><ShieldCheck size={21} /></span><div><h3>单品渗透明细</h3><p>{formatNumber(penetration.length)} 个城市</p></div>
          <button className="icon-button" type="button" onClick={() => void exportPenetration(penetration, targets.targetProduct, dateLabel)} aria-label="下载单品渗透明细"><Download size={17} /></button>
        </article>
      </section>

      <section className="dashboard-grid dashboard-grid-even data-info-grid">
        <article className="panel">
          <div className="panel-head"><div><span className="eyebrow">校验结果</span><h3>数据质量检查</h3></div></div>
          <div className="check-list">
            <div><CheckCircle2 size={17} /><span><strong>核心字段完整</strong><small>缺失产品、日期或终端：{missingCore} 行</small></span></div>
            <div className={duplicateCount ? 'check-warning' : ''}>{duplicateCount ? <TriangleAlert size={17} /> : <CheckCircle2 size={17} />}<span><strong>销售 ID 唯一性</strong><small>重复记录：{duplicateCount} 行</small></span></div>
            <div className={returns ? 'check-info' : ''}>{returns ? <TriangleAlert size={17} /> : <CheckCircle2 size={17} />}<span><strong>负数与退货</strong><small>{returns} 行，保留并计入净额</small></span></div>
            {dataset.meta.warnings.map((warning) => <div className="check-warning" key={warning}><TriangleAlert size={17} /><span><strong>导入提醒</strong><small>{warning}</small></span></div>)}
          </div>
        </article>
        <article className="panel privacy-card">
          <span className="privacy-icon"><LockKeyhole size={22} /></span>
          <div><span className="eyebrow">隐私设计</span><h3>原始 Excel 留在你的浏览器</h3><p>解析、筛选、聚合与导出都在本机完成。网页托管在 Cloudflare，但不会把销售明细上传到服务器。</p></div>
          {!dataset.meta.demo && <button className="text-button danger-text" type="button" onClick={onRestoreDemo}><RotateCcw size={15} /> 清除本地数据并恢复演示</button>}
        </article>
      </section>

      <section className="panel table-panel">
        <div className="panel-head"><div><span className="eyebrow">数据抽检</span><h3>前 30 条筛选后明细</h3></div><span className="panel-badge">仅供快速核对</span></div>
        <div className="table-scroll">
          <table>
            <thead><tr><th>销售时间</th><th>产品</th><th>品牌</th><th>数量</th><th>内部考核金额</th><th>终端名称</th><th>省市</th><th>流向类型</th><th>业务类型</th></tr></thead>
            <tbody>
              {records.slice(0, 30).map((record) => (
                <tr key={record.salesId}>
                  <td>{record.saleDate}</td><td className="max-cell"><strong className="cell-strong">{record.product}</strong></td><td>{record.brand}</td>
                  <td className={record.quantity < 0 ? 'negative-cell' : ''}>{formatNumber(record.quantity)}</td><td className={record.internalAmount < 0 ? 'negative-cell' : ''}>{formatCompactCurrency(record.internalAmount)}</td>
                  <td>{record.customerName}</td><td>{record.province} · {record.city}</td><td>{record.flowType}</td><td><span className={`status-pill ${record.quantity < 0 ? 'attention' : 'good'}`}>{record.movementType || (record.quantity < 0 ? '退货' : '纯销')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
